import React from 'react';
import Select from 'react-select';
import ColorPicker from '../../../Components/ColorPicker';

const ColorField = ({ id, label, edit, setEdit, themeColorKey, themeColorValue, propKey, fallback }) => (
  <div className='form-item'>
    <label className='form-lbl' style={{ color: 'var(--header-color)' }}>{label}</label>
    <ColorPicker
      id={id}
      color={themeColorValue(edit.props, propKey, fallback)}
      setColor={(c) => setEdit(prev => ({
        ...prev,
        props: { ...prev.props, [themeColorKey(propKey)]: c }
      }))}
    />
  </div>
);

const NumberField = ({ label, edit, setEdit, propKey, min, max, step, fallback }) => (
  <div className='form-item'>
    <label className='form-lbl' style={{ color: 'var(--header-color)' }}>{label}</label>
    <input
      type='number'
      className='add-column'
      min={min}
      max={max}
      step={step}
      style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
      value={edit.props[propKey] ?? fallback}
      onChange={(e) => setEdit(prev => ({
        ...prev,
        props: { ...prev.props, [propKey]: Number(e.target.value || 0) }
      }))}
    />
  </div>
);

const WaterNetworkWidgetSettings = ({
  edit,
  setEdit,
  t,
  directionLabels,
  themeColorKey,
  themeColorValue,
}) => {
  const pipeDirectionOptions = [
    { label: directionLabels.horizontal || 'Horizontal', value: 'horizontal' },
    { label: directionLabels.vertical || 'Vertical', value: 'vertical' },
  ];
  const horizontalFlowOptions = [
    { label: directionLabels.leftToRight || 'Left to Right', value: 'left-to-right' },
    { label: directionLabels.rightToLeft || 'Right to Left', value: 'right-to-left' },
  ];
  const verticalFlowOptions = [
    { label: directionLabels.topToBottom || 'Top to Bottom', value: 'top-to-bottom' },
    { label: directionLabels.bottomToTop || 'Bottom to Top', value: 'bottom-to-top' },
  ];
  const elbowDirectionOptions = [
    { label: directionLabels.topLeft || 'Top-Left', value: 'top-left' },
    { label: directionLabels.topRight || 'Top-Right', value: 'top-right' },
    { label: directionLabels.bottomLeft || 'Bottom-Left', value: 'bottom-left' },
    { label: directionLabels.bottomRight || 'Bottom-Right', value: 'bottom-right' },
  ];
  const pumpDirectionOptions = horizontalFlowOptions;

  const sharedColorFields = (prefix) => [
    { id: `${prefix}-water-color`, label: 'Water Flow Color', propKey: 'color', fallback: '#76B6EA' },
    { id: `${prefix}-shell-color`, label: 'Pipe Shell Color', propKey: 'pipeColor' },
    { id: `${prefix}-background-color`, label: 'Pipe Background Color', propKey: 'pipeBackgroundColor', fallback: 'rgba(18, 28, 40, 0.55)' },
    { id: `${prefix}-glass-color`, label: 'Pipe Glass Color', propKey: 'pipeGlassColor', fallback: themeColorValue(edit.props, 'pipeColor') },
    { id: `${prefix}-border-color`, label: 'Pipe Border Color', propKey: 'pipeBorderColor' },
    { id: `${prefix}-clamp-color`, label: 'Clamp Color', propKey: 'clampColor' },
  ];

  if (edit.type === 'pipe') {
    const flowOptions = edit.props.direction === 'vertical' ? verticalFlowOptions : horizontalFlowOptions;
    const fallbackFlow = edit.props.direction === 'vertical' ? verticalFlowOptions[0] : horizontalFlowOptions[0];

    return (
      <>
        <div className='form-item'>
          <label className='form-lbl' style={{ color: 'var(--header-color)' }}>{t.pipeDirectionLabel || 'Pipe Direction'}</label>
          <Select
            classNamePrefix="react-select"
            value={pipeDirectionOptions.find(o => o.value === edit.props.direction)}
            options={pipeDirectionOptions}
            onChange={selectedOption => {
              const nextDirection = selectedOption ? selectedOption.value : 'horizontal';
              const nextFlowDirection = nextDirection === 'vertical'
                ? (['top-to-bottom', 'bottom-to-top'].includes(edit.props.flowDirection) ? edit.props.flowDirection : 'top-to-bottom')
                : (['left-to-right', 'right-to-left'].includes(edit.props.flowDirection) ? edit.props.flowDirection : 'left-to-right');

              setEdit({
                ...edit,
                props: {
                  ...edit.props,
                  direction: nextDirection,
                  flowDirection: nextFlowDirection
                }
              });
            }}
            isClearable={false}
          />
        </div>

        <div className='form-item'>
          <label className='form-lbl' style={{ color: 'var(--header-color)' }}>{t.waterFlowDirectionLabel || 'Water Flow Direction'}</label>
          <Select
            classNamePrefix="react-select"
            value={flowOptions.find(o => o.value === edit.props.flowDirection) || fallbackFlow}
            options={flowOptions}
            onChange={selectedOption => setEdit({
              ...edit,
              props: {
                ...edit.props,
                flowDirection: selectedOption
                  ? selectedOption.value
                  : (edit.props.direction === 'vertical' ? 'top-to-bottom' : 'left-to-right')
              }
            })}
            isClearable={false}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
          <ColorField id="pipe-frame-bg" label="Frame Background" edit={edit} setEdit={setEdit} themeColorKey={themeColorKey} themeColorValue={themeColorValue} propKey="frameBg" />
          <ColorField id="pipe-frame-border-color" label="Frame Border Color" edit={edit} setEdit={setEdit} themeColorKey={themeColorKey} themeColorValue={themeColorValue} propKey="frameBorderColor" />
          <ColorField id="pipe-water-color" label="Water Color" edit={edit} setEdit={setEdit} themeColorKey={themeColorKey} themeColorValue={themeColorValue} propKey="color" />
          <ColorField id="pipe-shell-color" label="Pipe Shell Color" edit={edit} setEdit={setEdit} themeColorKey={themeColorKey} themeColorValue={themeColorValue} propKey="pipeColor" />
          <ColorField id="pipe-background-color" label="Pipe Background Color" edit={edit} setEdit={setEdit} themeColorKey={themeColorKey} themeColorValue={themeColorValue} propKey="pipeBackgroundColor" fallback="rgba(18, 28, 40, 0.55)" />
          <ColorField id="pipe-glass-color" label="Pipe Glass Color" edit={edit} setEdit={setEdit} themeColorKey={themeColorKey} themeColorValue={themeColorValue} propKey="pipeGlassColor" fallback={themeColorValue(edit.props, 'pipeColor')} />
          <ColorField id="pipe-border-color" label="Pipe Border Color" edit={edit} setEdit={setEdit} themeColorKey={themeColorKey} themeColorValue={themeColorValue} propKey="pipeBorderColor" />
          <ColorField id="pipe-clamp-color" label="Clamp Color" edit={edit} setEdit={setEdit} themeColorKey={themeColorKey} themeColorValue={themeColorValue} propKey="clampColor" />
          <NumberField label="Frame Border Width" edit={edit} setEdit={setEdit} propKey="frameBorderWidth" min="0" max="12" step="1" fallback={2} />
          <NumberField label="Frame Radius" edit={edit} setEdit={setEdit} propKey="frameRadius" min="0" max="80" step="1" fallback={18} />
          <NumberField label="Frame Padding" edit={edit} setEdit={setEdit} propKey="framePadding" min="0" max="80" step="1" fallback={0} />
          <NumberField label="Pipe Thickness" edit={edit} setEdit={setEdit} propKey="pipeHeight" min="20" max="140" step="1" fallback={70} />
          <NumberField label="Pipe Width Percent" edit={edit} setEdit={setEdit} propKey="pipeWidthPercent" min="20" max="100" step="1" fallback={100} />
          <NumberField label="Title Font Size" edit={edit} setEdit={setEdit} propKey="titleFontSize" min="8" max="120" step="1" fallback={34} />
          <NumberField label="Status Font Size" edit={edit} setEdit={setEdit} propKey="statusFontSize" min="8" max="140" step="1" fallback={30} />
        </div>
      </>
    );
  }

  if (edit.type === 'elbow') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
        <div className='form-item'>
          <label className='form-lbl' style={{ color: 'var(--header-color)' }}>{t.directionLabel || 'Direction'}</label>
          <Select
            classNamePrefix="react-select"
            value={elbowDirectionOptions.find(o => o.value === (edit.props.direction || 'top-right')) || elbowDirectionOptions[1]}
            options={elbowDirectionOptions}
            onChange={selectedOption => setEdit({
              ...edit,
              props: { ...edit.props, direction: selectedOption ? selectedOption.value : 'top-right' }
            })}
            isClearable={false}
          />
        </div>
        <NumberField label="Pipe Thickness" edit={edit} setEdit={setEdit} propKey="pipeHeight" min="40" max="120" step="1" fallback={70} />
        {sharedColorFields('elbow').map(field => (
          <ColorField key={field.id} {...field} edit={edit} setEdit={setEdit} themeColorKey={themeColorKey} themeColorValue={themeColorValue} />
        ))}
      </div>
    );
  }

  if (edit.type === 'water-pump') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
        <div className='form-item'>
          <label className='form-lbl' style={{ color: 'var(--header-color)' }}>{t.directionLabel || 'Direction'}</label>
          <Select
            classNamePrefix="react-select"
            value={pumpDirectionOptions.find(o => o.value === (edit.props.direction || 'left-to-right')) || pumpDirectionOptions[0]}
            options={pumpDirectionOptions}
            onChange={selectedOption => setEdit({
              ...edit,
              props: { ...edit.props, direction: selectedOption ? selectedOption.value : 'left-to-right' }
            })}
            isClearable={false}
          />
        </div>
        <NumberField label="Pipe / Elbow Thickness" edit={edit} setEdit={setEdit} propKey="pipeHeight" min="40" max="120" step="1" fallback={70} />
        {sharedColorFields('pump').map(field => (
          <ColorField key={field.id} {...field} edit={edit} setEdit={setEdit} themeColorKey={themeColorKey} themeColorValue={themeColorValue} />
        ))}
      </div>
    );
  }

  return null;
};

export default WaterNetworkWidgetSettings;
